import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {Alert, Button, Skeleton, Tooltip} from "@mui/material";
import {AutoAwesomeOutlined, ChevronRightOutlined} from "@mui/icons-material";
import {SegmentedControl} from "~/components/segmented-control/SegmentedControl.tsx";
import {formatAmount, formatDate} from "../contractExplorerTypes.ts";
import type {
    CoverageRow, LeakageMode, LeakageSupplier, SpendMixRow,
    useCoverageInsightQuery,
} from "../useContractInsightsQueries.ts";
import {
    useContractReportQuery,
    useGenerateReportMutation,
    useLeakageQuery,
    useLeakageTransactionsQuery,
    useOffContractMatrixQuery,
    useSpendMixQuery,
} from "../useContractInsightsQueries.ts";

type Props = {
    bagId: number;
    contractId: number;
    currency: string;
};

const pct = (value: number) => `${Math.round(value * 100)}%`;
const barWidth = (value: number, max: number) => `${Math.max(0, Math.min(100, (value / max) * 100))}%`;


/**
 * The Insights tab: three sections computed live from the links and the
 * databag's transactions. Material/service identity everywhere is the golden
 * record (harmonized names), never raw descriptions.
 *
 * Each section is one flat surface card (the `.cd-summary` / status-card
 * idiom), so the sections read as separate units. Bar colors: navy for the
 * spend mix (the app's chart-bar color); a lighter navy for leakage and the
 * in-contract bucket, orange = covered by another contract, and white = off
 * contract (it reads as the uncovered remainder of the bar).
 */
export const ContractInsightsPanel: React.FC<Props> = ({bagId, contractId, currency}) => {
    const spendMix = useSpendMixQuery(bagId, contractId, true);
    const offContract = useOffContractMatrixQuery(bagId, contractId, true);

    return (
        <div className="ci-panel">
            <SpendMixSection query={spendMix} currency={currency}/>
            {/* CoverageSection (the in/other/off stacked bars) is kept in code
                but not rendered for now — the matrix below replaced it. */}
            <OffContractSection query={offContract} currency={currency}/>
            {/* keyed: stepping to another contract remounts the section,
                resetting the drill and the by-item/by-category mode */}
            <LeakageSection key={contractId} bagId={bagId} contractId={contractId}
                            currency={currency}/>
            {/* keyed so a generation in flight doesn't paint on another contract */}
            <ReportSection key={`report-${contractId}`} bagId={bagId} contractId={contractId}/>
        </div>
    );
};

const SectionCard: React.FC<{
    title: string;
    hint: string;
    meta?: React.ReactNode;
    children: React.ReactNode;
}> = ({title, hint, meta, children}) => (
    <section className="ci-section">
        <header className="ci-head">
            <div className="ci-head-text">
                <h3 className="ci-title">{title}</h3>
                <p className="ci-hint">{hint}</p>
            </div>
            {meta && <div className="ci-meta">{meta}</div>}
        </header>
        {children}
    </section>
);

// ---- 1. contract spend mix ---------------------------------------------------

const SpendMixSection: React.FC<{query: ReturnType<typeof useSpendMixQuery>; currency: string}> = (
    {query, currency},
) => {
    const {data, isPending, isError, error} = query;
    if (isError) return <Alert severity="error">Spend mix failed: {error?.message}</Alert>;
    return (
        <SectionCard
            title="In-contract purchases"
            hint="What the linked spend actually buys"
            meta={data && data.total_spend > 0 && formatAmount(data.total_spend, currency)}>
            {isPending && <Skeleton variant="rounded" height={160}/>}
            {data && data.total_spend === 0 && (
                <p className="cd-empty">No linked spend yet.</p>
            )}
            {data && data.total_spend > 0 && (
                <div className="ci-columns">
                    <SpendMixList title="Purchased items" rows={data.materials} currency={currency}/>
                    <SpendMixList title="Categories" rows={data.categories} currency={currency}/>
                </div>
            )}
        </SectionCard>
    );
};

const SpendMixList: React.FC<{title: string; rows: SpendMixRow[]; currency: string}> = (
    {title, rows, currency},
) => {
    const max = Math.max(...rows.map(r => r.spend), 1);
    return (
        <div className="ci-column">
            <h4 className="ci-subtitle">{title}</h4>
            {rows.length === 0 && <p className="cd-empty">No data.</p>}
            {rows.map(row => (
                <div key={row.name} className="ci-bar-row">
                    <span className="ci-bar-label" title={row.name}>{row.name}</span>
                    <span className="ci-bar-track">
                        <span className="ci-bar ci-bar-mix" style={{width: barWidth(row.spend, max)}}/>
                    </span>
                    <span className="ci-bar-amount">{formatAmount(row.spend, currency)}</span>
                    <span className="ci-bar-share">{pct(row.share)}</span>
                </div>
            ))}
        </div>
    );
};

// ---- 2. supplier spend coverage ------------------------------------------------

// Kept (exported) but currently not rendered: the Supplier off-contract
// matrix below replaced it in the panel.
export const CoverageSection: React.FC<{query: ReturnType<typeof useCoverageInsightQuery>; currency: string}> = (
    {query, currency},
) => {
    const {data, isPending, isError, error} = query;
    if (isError) return <Alert severity="error">Coverage failed: {error?.message}</Alert>;
    return (
        <SectionCard
            title="Scope coverage"
            hint={`How much of ${data?.supplier_name ?? 'the supplier'}’s spend `
                + 'is in this contract, covered by another contract, or off contract'}>
            {isPending && <Skeleton variant="rounded" height={160}/>}
            {data && !data.available && (
                <Alert severity="info">{data.reason}</Alert>
            )}
            {data?.available && (
                <>
                    {/* The dots double as the legend for the stacked bars below. */}
                    <div className="ci-stats">
                        <div className="ci-stat">
                            <span className="ci-stat-label">Supplier spend</span>
                            <span className="ci-stat-value">{formatAmount(data.supplier_spend, currency)}</span>
                        </div>
                        <BucketStat dot="in" label="In contract" value={data.in_contract}
                                    total={data.supplier_spend} currency={currency}/>
                        <BucketStat dot="other" label="Covered by another contract" value={data.other_contract}
                                    total={data.supplier_spend} currency={currency}/>
                        <BucketStat dot="off" label="Off contract" value={data.off_contract}
                                    total={data.supplier_spend} currency={currency}/>
                    </div>
                    <div className="ci-columns">
                        <CoverageBars title="By entity" rows={data.entities}/>
                        <CoverageBars title="By material / service" rows={data.materials}/>
                    </div>
                </>
            )}
        </SectionCard>
    );
};

const BucketStat: React.FC<{
    dot: 'in' | 'other' | 'off';
    label: string;
    value: number;
    total: number;
    currency: string;
}> = ({dot, label, value, total, currency}) => (
    <div className="ci-stat">
        <span className="ci-stat-label"><i className={`ci-dot ci-dot-${dot}`}/>{label}</span>
        <span className="ci-stat-value">
            {formatAmount(value, currency)}
            {total > 0 && value > 0 && <em>{pct(value / total)}</em>}
        </span>
    </div>
);

const CoverageBars: React.FC<{title: string; rows: CoverageRow[]}> = ({title, rows}) => (
    <div className="ci-column">
        <h4 className="ci-subtitle">{title}<span className="ci-subtitle-note">% under contract</span></h4>
        {rows.map(row => {
            const total = row.spend || 1;
            return (
                <div key={row.name} className="ci-bar-row">
                    <span className="ci-bar-label" title={row.name}>{row.name}</span>
                    <Tooltip title={`In contract ${formatAmount(row.in_contract)} · other contract `
                        + `${formatAmount(row.other_contract)} · off contract ${formatAmount(row.off_contract)}`}>
                        <span className="ci-bar-track">
                            <span className="ci-bar ci-in" style={{width: barWidth(row.in_contract, total)}}/>
                            <span className="ci-bar ci-other" style={{width: barWidth(row.other_contract, total)}}/>
                            <span className="ci-bar ci-off" style={{width: barWidth(row.off_contract, total)}}/>
                        </span>
                    </Tooltip>
                    <span className="ci-bar-share">{pct(row.contracted_share)}</span>
                </div>
            );
        })}
    </div>
);

// ---- 2b. supplier off-contract matrix ----------------------------------------------

const OffContractSection: React.FC<{
    query: ReturnType<typeof useOffContractMatrixQuery>;
    currency: string;
}> = ({query, currency}) => {
    const {data, isPending, isError, error} = query;
    if (isError) return <Alert severity="error">Off-contract failed: {error?.message}</Alert>;
    return (
        <SectionCard
            title="Supplier off-contract"
            hint={`${data?.supplier_name ?? 'The supplier'}’s spend with no `
                + 'contract coverage, per entity and item — hover a cell for its share'}
            meta={data?.available && data.total_off > 0 && formatAmount(data.total_off, currency)}>
            {isPending && <Skeleton variant="rounded" height={160}/>}
            {data && !data.available && (
                <Alert severity="info">{data.reason}</Alert>
            )}
            {data?.available && data.items.length === 0 && (
                <p className="cd-empty">
                    No off-contract spend — everything this supplier billed is
                    covered by this or another contract.
                </p>
            )}
            {data?.available && data.items.length > 0 && (
                <div className="ci-matrix-scroll">
                    <table className="ci-matrix">
                        <thead>
                            <tr>
                                <th className="ci-matrix-item">Item</th>
                                {data.entities.map(entity => (
                                    <th key={entity} title={entity}>{entity}</th>
                                ))}
                                <th className="ci-matrix-rowtotal">Off contract</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map(row => (
                                <tr key={row.name}>
                                    <th className="ci-matrix-item" scope="row" title={row.name}>
                                        {row.name}
                                    </th>
                                    {row.cells.map((cell, index) => (
                                        cell === null
                                            ? <td key={data.entities[index]} className="ci-matrix-empty">—</td>
                                            : (
                                                <Tooltip
                                                    key={data.entities[index]}
                                                    title={`${pct(cell.off_share)} of `
                                                        + `${formatAmount(cell.total, currency)} spend is off `
                                                        + `contract (${cell.transactions} transaction`
                                                        + `${cell.transactions === 1 ? '' : 's'})`}>
                                                    <td style={{
                                                        // off-contract orange, deeper = larger share
                                                        backgroundColor:
                                                            `rgba(239, 108, 0, ${0.06 + cell.off_share * 0.3})`,
                                                    }}>
                                                        {formatAmount(cell.off, currency)}
                                                    </td>
                                                </Tooltip>
                                            )
                                    ))}
                                    <td className="ci-matrix-rowtotal">{formatAmount(row.off_total, currency)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </SectionCard>
    );
};

// ---- 3. maverick spend -----------------------------------------------------------

const LEAKAGE_MODE_ORDER: LeakageMode[] = ['item', 'category'];
const LEAKAGE_MODE_LABELS = ['By item', 'By category'];

const LeakageSection: React.FC<{
    bagId: number;
    contractId: number;
    currency: string;
}> = ({bagId, contractId, currency}) => {
    const [mode, setMode] = React.useState<LeakageMode>('item');
    const {data, isPending, isError, error} = useLeakageQuery(bagId, contractId, true, mode);
    const [supplier, setSupplier] = React.useState<LeakageSupplier | null>(null);
    const [material, setMaterial] = React.useState<string | null>(null);
    const transactions = useLeakageTransactionsQuery(
        bagId, contractId, supplier?.supplier_id ?? null, material, mode);

    if (isError) return <Alert severity="error">Leakage failed: {error?.message}</Alert>;

    const supplierMax = Math.max(...(data?.suppliers.map(s => s.leakage) ?? []), 1);
    const materialMax = Math.max(...(supplier?.materials.map(m => m.spend) ?? []), 1);

    return (
        <SectionCard
            title="Maverick spend"
            hint={'Uncontracted spend '
                + (mode === 'item'
                    ? "on this contract's items, across all suppliers"
                    : "in this contract's categories, across all suppliers")}
            meta={
                <>
                    <SegmentedControl
                        className="ci-mode"
                        labels={LEAKAGE_MODE_LABELS}
                        value={LEAKAGE_MODE_ORDER.indexOf(mode)}
                        onChange={index => {
                            setMode(LEAKAGE_MODE_ORDER[index]);
                            setSupplier(null);
                            setMaterial(null);
                        }}/>
                    {data && data.suppliers.length > 0 && (
                        <span>{formatAmount(data.total, currency)}</span>
                    )}
                </>
            }>
            {isPending && <Skeleton variant="rounded" height={160}/>}
            {data && data.suppliers.length === 0 && (
                <p className="cd-empty">
                    No uncontracted spend found in this contract's
                    {mode === 'item' ? ' item' : ' category'} scope.
                </p>
            )}
            {data && data.suppliers.length > 0 && (
                <div className="ci-column">
                    <h4 className="ci-subtitle">By supplier<span className="ci-subtitle-note">click to drill down</span></h4>
                    {data.suppliers.map(row => {
                        const open = supplier?.supplier_id === row.supplier_id;
                        return (
                            <React.Fragment key={row.supplier_id}>
                                <button type="button"
                                        className={`ci-bar-row ci-clickable${open ? ' is-open' : ''}`}
                                        onClick={() => {
                                            setSupplier(open ? null : row);
                                            setMaterial(null);
                                        }}>
                                    <span className="ci-bar-label" title={row.name}>{row.name}</span>
                                    <span className="ci-bar-track">
                                        <span className="ci-bar ci-leak"
                                              style={{width: barWidth(row.leakage, supplierMax)}}/>
                                    </span>
                                    <span className="ci-bar-amount">{formatAmount(row.leakage, currency)}</span>
                                    <span className="ci-bar-share">{pct(row.share_of_supplier_spend)} of supplier spend</span>
                                    <ChevronRightOutlined className="ci-bar-chevron"/>
                                </button>
                                {open && (
                                    <div className="ci-sub">
                                        {row.materials.map(mat => (
                                            <React.Fragment key={mat.name}>
                                                <button type="button"
                                                        className={'ci-bar-row ci-clickable'
                                                            + (material === mat.name ? ' is-open' : '')}
                                                        onClick={() => setMaterial(
                                                            material === mat.name ? null : mat.name)}>
                                                    <span className="ci-bar-label" title={mat.name}>{mat.name}</span>
                                                    <span className="ci-bar-track">
                                                        <span className="ci-bar ci-leak"
                                                              style={{width: barWidth(mat.spend, materialMax)}}/>
                                                    </span>
                                                    <span className="ci-bar-amount">{formatAmount(mat.spend, currency)}</span>
                                                    <span className="ci-bar-share">
                                                        {mat.transactions} transaction{mat.transactions === 1 ? '' : 's'}
                                                    </span>
                                                    <ChevronRightOutlined className="ci-bar-chevron"/>
                                                </button>
                                                {material === mat.name && (
                                                    <div className="ci-sub">
                                                        {transactions.isPending
                                                            ? <Skeleton variant="rounded" height={100}/>
                                                            : (
                                                                <table className="cd-table">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>Transaction</th><th>Date</th><th>Entity</th>
                                                                            <th className="cd-num">Spend ({currency})</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(transactions.data?.transactions ?? []).map(tx => (
                                                                            <tr key={tx.p_id}>
                                                                                <td>
                                                                                    <span className="cl-desc">{tx.description || tx.p_id}</span>
                                                                                    {tx.description && <span className="cd-sub">{tx.p_id}</span>}
                                                                                </td>
                                                                                <td>{formatDate(tx.date)}</td>
                                                                                <td>{tx.entity}</td>
                                                                                <td className="cd-num">{formatAmount(tx.spend)}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            )}
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            )}
        </SectionCard>
    );
};

// ---- 4. AI contract review -------------------------------------------------------

const ReportSection: React.FC<{bagId: number; contractId: number}> = ({bagId, contractId}) => {
    const {data, isPending, isError, error} = useContractReportQuery(bagId, contractId, true);
    const generate = useGenerateReportMutation();
    const busy = generate.isPending || Boolean(data?.generating);

    return (
        <SectionCard
            title="AI contract review"
            hint="Generated from the contract document and its linked transactions — review before acting on it"
            meta={
                <Button
                    size="small"
                    variant={data?.available ? 'outlined' : 'contained'}
                    startIcon={<AutoAwesomeOutlined/>}
                    disabled={busy || isPending}
                    onClick={() => generate.mutate({bagId, contractId})}>
                    {busy ? 'Generating\u2026' : data?.available ? 'Regenerate' : 'Generate report'}
                </Button>
            }>
            {isPending && <Skeleton variant="rounded" height={80}/>}
            {isError && <Alert severity="error">Report failed to load: {error?.message}</Alert>}
            {generate.isError && (
                <Alert severity="error" className="ci-report-alert">
                    Could not generate the report: {generate.error?.message}
                </Alert>
            )}
            {busy && <Skeleton variant="rounded" height={160}/>}
            {!busy && data && !data.available && !generate.isError && (
                <p className="cd-empty">
                    No report yet. It reads the contract's header, payment terms and items together
                    with the transactions linked to it, and writes up price variance, payment-term
                    mismatches, utilization, scope drift and renewal advice.
                </p>
            )}
            {!busy && data?.available && (
                <>
                    {data.stale && (
                        <Alert severity="warning" className="ci-report-alert">
                            The contract or its transactions changed since this report was
                            written — regenerate it.
                        </Alert>
                    )}
                    <div className="ci-md">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {data.report_markdown ?? ''}
                        </ReactMarkdown>
                    </div>
                    <p className="ci-report-caption">
                        Generated {formatDate(data.generated_at ?? null)} — AI-written; verify figures before acting.
                    </p>
                </>
            )}
        </SectionCard>
    );
};
